/**
 * AuthService
 * Handles AWS Cognito authentication using AWS Amplify v6
 * 
 * Requirements: 9.1, 9.2, 9.3, 9.5, 11.4
 */

import { Amplify } from 'aws-amplify';
import {
  signOut,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  AuthUser,
} from 'aws-amplify/auth';
import config from '../config';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export class AuthService {
  private static instance: AuthService | null = null;
  private currentUser: AuthUser | null = null;
  private tokens: AuthTokens | null = null;

  private constructor() {
    console.log('='.repeat(60));
    console.log('🚀 AuthService v2.1 - OAuth Fix with signInWithRedirect');
    console.log('='.repeat(60));
    this.initializeAmplify();
  }

  /**
   * Get singleton instance
   */
  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Initialize AWS Amplify with Cognito configuration
   * Requirement 9.1, 9.2
   */
  private initializeAmplify(): void {
    const amplifyConfig = {
      Auth: {
        Cognito: {
          userPoolId: config.cognito.userPoolId,
          userPoolClientId: config.cognito.clientId,
          loginWith: {
            oauth: {
              domain: config.cognito.domain,
              scopes: ['email', 'openid', 'phone'],
              redirectSignIn: [config.cognito.redirectUri],
              redirectSignOut: [config.cognito.redirectUri],
              responseType: 'code' as const,
            },
          },
        },
      },
    };

    console.log('[AuthService] Configuring Amplify with:', {
      userPoolId: config.cognito.userPoolId,
      clientId: config.cognito.clientId,
      domain: config.cognito.domain,
      redirectUri: config.cognito.redirectUri,
    });

    Amplify.configure(amplifyConfig);

    console.log('[AuthService] AWS Amplify configured for Cognito Hosted UI authentication');
  }

  /**
   * Redirect to Cognito Hosted UI for login
   * Requirements: 9.2, 9.3
   */
  async login(): Promise<AuthUser> {
    try {
      console.log('[AuthService] Starting OAuth login with Amplify signInWithRedirect...');
      
      // Use Amplify's built-in OAuth handling
      await signInWithRedirect();
      
      // This will never return as we're redirecting
      return null as any;
    } catch (error) {
      console.error('[AuthService] Login failed:', error);
      throw error;
    }
  }

  /**
   * Redirect to Cognito Hosted UI for registration/signup
   * This directs users to the signup form instead of login form
   */
  async register(): Promise<void> {
    try {
      console.log('[AuthService] Starting OAuth registration redirect...');
      
      // Construct Cognito Hosted UI signup URL manually
      const signupUrl = `https://${config.cognito.domain}/signup?` +
        `client_id=${config.cognito.clientId}&` +
        `response_type=code&` +
        `scope=email+openid+phone&` +
        `redirect_uri=${encodeURIComponent(config.cognito.redirectUri)}`;
      
      console.log('[AuthService] Redirecting to Cognito signup:', signupUrl);
      window.location.href = signupUrl;
      
    } catch (error) {
      console.error('[AuthService] Registration redirect failed:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback after Cognito redirect
   * Call this on your redirect page to complete authentication
   */
  async handleOAuthCallback(): Promise<boolean> {
    try {
      console.log('[AuthService] handleOAuthCallback started');
      
      // Check if we have an authorization code in the URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      
      console.log('[AuthService] Authorization code:', code ? 'present' : 'missing');
      console.log('[AuthService] OAuth state:', state ? 'present' : 'missing');
      console.log('[AuthService] Full URL:', window.location.href);
      
      // Check for invitation code that might have been stored during registration
      const storedInvitationCode = localStorage.getItem('invitation_code');
      if (storedInvitationCode) {
        console.log('[AuthService] Found stored invitation code for OAuth callback');
      }
      
      if (!code) {
        console.log('[AuthService] No authorization code found');
        return false;
      }

      // Amplify v6 automatically handles the token exchange in the background
      // We need to wait for it to complete and retry if necessary
      console.log('[AuthService] Waiting for Amplify to process OAuth callback...');
      
      let retries = 0;
      const maxRetries = 10;
      const retryDelay = 500; // Start with 500ms
      
      while (retries < maxRetries) {
        try {
          console.log(`[AuthService] Attempt ${retries + 1}/${maxRetries} to get current user...`);
          
          // Try to get the current user
          this.currentUser = await getCurrentUser();
          console.log('[AuthService] Current user retrieved:', this.currentUser.username);
          
          // If we got the user, fetch and store tokens
          console.log('[AuthService] Fetching and storing tokens...');
          await this.fetchAndStoreTokens();
          console.log('[AuthService] Tokens stored successfully');

          console.log('[AuthService] OAuth login successful:', this.currentUser.username);
          
          // Don't clean up URL here - let the component handle navigation
          return true;
          
        } catch (userError) {
          console.log(`[AuthService] Attempt ${retries + 1} failed:`, userError);
          retries++;
          
          if (retries < maxRetries) {
            // Wait before retrying, with exponential backoff
            const delay = retryDelay * Math.pow(1.5, retries - 1);
            console.log(`[AuthService] Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      // If we've exhausted all retries, return false
      console.error('[AuthService] All retry attempts failed');
      return false;
      
    } catch (error) {
      console.error('[AuthService] OAuth callback failed:', error);
      if (error instanceof Error) {
        console.error('[AuthService] Error message:', error.message);
        console.error('[AuthService] Error stack:', error.stack);
        console.error('[AuthService] Error name:', error.name);
      }
      return false;
    }
  }

  /**
   * Logout and revoke Cognito session
   * Requirement 11.4
   */
  async logout(): Promise<void> {
    try {
      console.log('[AuthService] Starting logout...');
      
      // Clear local state first
      this.currentUser = null;
      this.tokens = null;
      this.clearLocalStorage();

      // Sign out from Cognito locally
      await signOut();

      // Manually redirect to Cognito logout endpoint
      // Amplify's signOut({ global: true }) doesn't work correctly with Hosted UI
      const logoutUrl = `https://${config.cognito.domain}/logout?client_id=${config.cognito.clientId}&logout_uri=${encodeURIComponent(config.cognito.redirectUri)}`;
      
      console.log('[AuthService] Redirecting to Cognito logout:', logoutUrl);
      window.location.href = logoutUrl;

      console.log('[AuthService] Logout successful');
    } catch (error) {
      console.error('[AuthService] Logout failed:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   * Requirement 9.5
   */
  async refreshToken(): Promise<string> {
    try {
      // Fetch new session (Amplify handles refresh automatically)
      const session = await fetchAuthSession({ forceRefresh: true });

      if (!session.tokens) {
        throw new Error('No tokens available after refresh');
      }

      // Update stored tokens
      this.tokens = {
        accessToken: session.tokens.accessToken.toString(),
        refreshToken: '', // Amplify v6 manages refresh tokens internally
        idToken: session.tokens.idToken?.toString() || '',
        expiresAt: session.tokens.accessToken.payload.exp
          ? session.tokens.accessToken.payload.exp * 1000
          : Date.now() + 3600000,
      };

      // Store in local storage
      // Requirement 9.3
      this.storeTokensInLocalStorage();

      console.log('Token refreshed successfully');
      return this.tokens.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Get current access token
   * Requirement 9.4
   */
  getAccessToken(): string | null {
    return this.tokens?.accessToken || null;
  }

  /**
   * Get current ID token
   */
  getIdToken(): string | null {
    return this.tokens?.idToken || null;
  }

  /**
   * Get all tokens
   */
  getTokens(): AuthTokens | null {
    return this.tokens;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUser !== null && this.tokens !== null;
  }

  /**
   * Get current user
   */
  getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Get user ID (sub claim from token)
   */
  getUserId(): string | null {
    return this.currentUser?.userId || null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    if (!this.tokens) {
      return true;
    }

    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

    return now >= this.tokens.expiresAt - bufferTime;
  }

  /**
   * Get access token with automatic refresh if expired
   * Requirement 9.5
   */
  async getValidAccessToken(): Promise<string> {
    // Check if we have tokens
    if (!this.tokens) {
      // Try to restore from session
      await this.restoreSession();
    }

    // Check if token is expired
    if (this.isTokenExpired()) {
      console.log('Token expired, refreshing...');
      return await this.refreshToken();
    }

    return this.tokens!.accessToken;
  }

  /**
   * Restore session from Amplify (checks if user is still signed in)
   */
  async restoreSession(): Promise<boolean> {
    try {
      // Check if user is signed in
      this.currentUser = await getCurrentUser();

      // Fetch tokens
      await this.fetchAndStoreTokens();

      console.log('Session restored:', this.currentUser.username);
      return true;
    } catch (error) {
      console.log('No active session found');
      return false;
    }
  }

  /**
   * Fetch tokens from Amplify session and store them
   * Requirements: 9.2, 9.3
   */
  private async fetchAndStoreTokens(): Promise<void> {
    const session = await fetchAuthSession();

    if (!session.tokens) {
      throw new Error('No tokens available in session');
    }

    this.tokens = {
      accessToken: session.tokens.accessToken.toString(),
      refreshToken: '', // Amplify v6 manages refresh tokens internally
      idToken: session.tokens.idToken?.toString() || '',
      expiresAt: session.tokens.accessToken.payload.exp
        ? session.tokens.accessToken.payload.exp * 1000
        : Date.now() + 3600000,
    };

    // Store in local storage
    // Requirement 9.3
    this.storeTokensInLocalStorage();
  }

  /**
   * Store tokens in local storage
   * Requirement 9.3
   */
  private storeTokensInLocalStorage(): void {
    if (!this.tokens) {
      return;
    }

    try {
      localStorage.setItem('textile_vision_tokens', JSON.stringify(this.tokens));
      localStorage.setItem('textile_vision_user', JSON.stringify({
        userId: this.currentUser?.userId,
        username: this.currentUser?.username,
      }));
    } catch (error) {
      console.error('Failed to store tokens in local storage:', error);
    }
  }

  /**
   * Clear local storage
   * Requirement 11.4
   */
  private clearLocalStorage(): void {
    try {
      localStorage.removeItem('textile_vision_tokens');
      localStorage.removeItem('textile_vision_user');
    } catch (error) {
      console.error('Failed to clear local storage:', error);
    }
  }


}

export default AuthService;
